import { Box, ButtonProps, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/common/Button';
import Checkbox from '../../components/common/Checkbox';
import Chips from '../../components/common/Chips';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgNumberFilter from '../../components/common/customAgGrid/CustomAgNumberFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import Dropdown from '../../components/common/Dropdown';
import InputField from '../../components/common/InputField';
import Modal from '../../components/common/Modal';
import Stack from '@mui/material/Stack';
import AddTypeInProductModal from '../../components/lubricationChart/AddTypeInProductModal';
import type { TypeInProductFormValues } from '../../components/lubricationChart/AddTypeInProductModal';
import LubricationProductListing from '../../components/lubricationChart/LubricationProductListing';
import PageHeader from '../../components/PageHeader';
import UnsavedChangesModal from '../../components/UnsavedChangesModal';
import { useGetCompaniesMakersListQuery } from '../../store/apiSlices/companies/companiesMakers/companiesMakersApis';
import { useGetLubricantCategoriesListQuery } from '../../store/apiSlices/lubricants/lubricantCategory/lubricantCategoryApis';
import { useGetLubricantGroupsListQuery } from '../../store/apiSlices/lubricants/lubricantGroups/lubricantGroupsApis';
import {
  useCreateLubricantProductMutation,
  useGetUnitsOfMeasurementQuery,
} from '../../store/apiSlices/lubricants/lubricantProducts/lubricantProductsApis';
import { useGetLubricantTypesListQuery } from '../../store/apiSlices/lubricants/lubricantTypes/lubricantTypesApis';
import {
  useAddProductToVesselMutation,
  useDeleteVesselProductMutation,
  useGetAllProductsNotInVesselQuery,
  useGetLubricationProductsByVesselQuery,
  useUpdateVesselProductStatusMutation,
} from '../../store/apiSlices/lubricationChart/lubricationChartsApis';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { useGetTypeInProductsListQuery } from '../../store/apiSlices/typeInProducts/typeInProductsApis';

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

interface ActionItem extends Omit<ButtonProps, 'children' | 'variant'> {
  label: string;
  route?: string;
  onClick?: () => void;
  variant?: 'blue' | 'outlinedBlue' | 'red' | 'gray';
}

interface LubricationChart {
  vessel: string;
  manager: string;
  activeProducts: number;
  typeInProduct: number;
  inactiveProducts: number;
  typeIn: number;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
}

// ---------- Types ----------
export interface LubricantProduct {
  id: number;
  name: string;
  makerId: number;
  categoryId: number;
  categoryName: string;
  typeId: number | null;
  typeName: string | null;
  productGroupId: number | null;
  productGroupName: string | null;
  uomId: number;
  active: boolean;
  typeIn: boolean;
  typeInManagerId: number | null;
  updatedBy: number;
  updatedOn: string;
}

// Custom cell renderer for checkbox selection
const CheckboxCellRenderer = ({ data, selectedProducts, onProductSelect }: any) => {
  return (
    <Checkbox
      checked={selectedProducts.includes(data.id)}
      onChange={() => onProductSelect(data.id)}
    />
  );
};

const LubricationDetailContainer = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const rowData: LubricationChart = location.state?.data;
  // Mode follows the route so /lubrication-view can never render the edit UI
  const mode: 'view' | 'edit' = location.pathname === '/lubrication-edit' ? 'edit' : 'view';
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [openTypeInModal, setOpenTypeInModal] = useState(false);

  const {
    control: typeInControl,
    handleSubmit: handleTypeInSubmit,
    reset: resetTypeInForm,
    formState: { errors: typeInErrors, isValid: isTypeInFormValid },
  } = useForm<TypeInProductFormValues>({
    mode: 'onChange',
    defaultValues: {
      productName: '',
      maker: '',
      category: '',
      type: '',
      unit: '',
      status: 'true',
    },
  });
  const [openEditStatusModal, setOpenEditStatusModal] = useState(false);
  const [editProductData, setEditProductData] = useState<any>(null);
  const [editProductFormData, setEditProductFormData] = useState({
    productName: '',
    status: '',
  });
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [tableHeight, setTableHeight] = useState<number>(400);

  // Refs for measuring heights
  const headerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const statusOptions = [
    { value: 'true', label: t('common.active') },
    { value: 'false', label: t('common.inactive') },
  ];

  const {
    data: lubricationChartsListData = [],
    isLoading: isLoadingLubricationChartsList,
    refetch: refetchProducts,
  } = useGetLubricationProductsByVesselQuery(location.state?.data?.vesselId, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const {
    data: masterCatalogueData = [],
    isLoading: isLoadingMasterCatalogue,
    refetch: refetchMasterCatalogue,
  } = useGetAllProductsNotInVesselQuery(location.state?.data?.vesselId, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [addPToductToVessel] = useAddProductToVesselMutation();
  const [createLubricantProduct, { isLoading: isCreating }] = useCreateLubricantProductMutation();
  const { data: unitsData } = useGetUnitsOfMeasurementQuery();
  const { data: makersData } = useGetCompaniesMakersListQuery({});
  const { data: categoriesData } = useGetLubricantCategoriesListQuery({});
  const { data: typesData } = useGetLubricantTypesListQuery({});
  const { data: groupsData } = useGetLubricantGroupsListQuery({});
  const [updateProduct] = useUpdateVesselProductStatusMutation();
  const [deleteVesselProduct, { isLoading: isDeleting }] = useDeleteVesselProductMutation();

  // Get refetch function for type-in products to update sidebar count
  const { refetch: refetchTypeInProducts } = useGetTypeInProductsListQuery();

  // Transform API data to display format
  const masterCatalogueList = useMemo(() => {
    // Check if masterCatalogueData exists and has the expected structure
    if (
      !masterCatalogueData ||
      Array.isArray(masterCatalogueData) ||
      !masterCatalogueData.data?.content
    ) {
      return [];
    }

    return masterCatalogueData?.data?.content?.map((product: any) => {
      return {
        id: product.id,
        productName: product.name,
        maker: product.makerName || '-',
        category: product.categoryName || '-',
        type: product.typeName || '-',
        unit: product.symbol || '-',
        status: product.active ? 'Active' : 'Inactive',
        lastUpdatedBy: product.updatedBy?.toString() || '-',
        lastUpdatedOn: dayjs(product.updatedOn).format('DD/MM/YYYY HH:mm'),
      };
    });
  }, [masterCatalogueData]);

  const handleOpenModal = useCallback(() => {
    refetchMasterCatalogue(); // Refetch data when opening modal
    setOpenModal(true);
  }, [refetchMasterCatalogue]);

  const handleAddProduct = useCallback(() => {
    handleOpenModal();
  }, [handleOpenModal]);

  useEffect(() => {
    if (mode === 'edit') {
      return setActions([
        {
          label: t('lubricants.addProduct'),
          variant: 'blue',
          onClick: () => {
            handleAddProduct();
          },
        },
      ]);
    }
  }, [handleAddProduct, mode, t]);

  useEffect(() => {
    setEditProductFormData((prev) => ({
      ...prev,
      status: editProductData?.status?.toString(),
    }));
  }, [editProductData]);

  // Calculate table height based on available space
  useEffect(() => {
    const calculateHeight = () => {
      if (headerRef.current && containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const headerHeight = headerRef.current.clientHeight;
        // Subtract header height and add some padding buffer (24px for spacing)
        const availableHeight = containerHeight - headerHeight - 24;
        setTableHeight(Math.max(availableHeight, 300)); // Minimum 300px
      }
    };

    // Calculate on mount and when window resizes
    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    // Use ResizeObserver for more accurate tracking
    const resizeObserver = new ResizeObserver(calculateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', calculateHeight);
      resizeObserver.disconnect();
    };
  }, []);
  const onProductSelect = useCallback((id: string) => {
    setSelectedProducts((prev: number[]) => {
      if (prev.includes(Number(id))) {
        return prev.filter((item) => item !== Number(id));
      } else {
        return [...prev, Number(id)];
      }
    });
  }, []);

  const masterCatalogueColumns: ColDef[] = useMemo(
    () => [
      {
        field: 'select',
        headerName: t('common.select'),
        width: 70,
        maxWidth: 70,
        flex: 0,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => (
          <CheckboxCellRenderer
            data={params.data}
            selectedProducts={selectedProducts}
            onProductSelect={onProductSelect}
          />
        ),
      },
      {
        field: 'id',
        headerName: t('table.columns.id'),
        width: 100,
        maxWidth: 100,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        sortable: true,
        floatingFilterComponent: CustomAgNumberFilter,
      },
      {
        field: 'productName',
        headerName: t('table.columns.productName'),
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        sortable: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'maker',
        headerName: t('table.columns.maker'),
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        sortable: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'category',
        headerName: t('table.columns.category'),
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        sortable: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'type',
        headerName: t('table.columns.type'),
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        sortable: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'unit',
        headerName: t('table.columns.unit'),
        width: 150,
        maxWidth: 120,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        sortable: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        width: 150,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        sortable: true,
        cellRenderer: (params: any) => <Chips width="100px" label={params.data.status} />,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
    ],
    [selectedProducts, onProductSelect, t],
  );

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    {
      label: t('lubricationChart.title'),
      isActive: false,
      onClick: () => navigate('/lubrication-charts'),
    },
    {
      label: rowData?.vessel || '',
      isActive: true,
      onClick: () => {},
    },
  ];

  // If no data is passed, redirect back
  if (!rowData) {
    return <Navigate to="/lubrication-charts" replace />;
  }

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCloseUnsavedChangesModal = () => {
    setShowUnsavedChangesModal(false);
  };

  const handleLeaveWithoutSaving = () => {
    setShowUnsavedChangesModal(false);
    navigate('/lubrication-charts');
  };

  const handleCloseTypeInModal = () => {
    setOpenTypeInModal(false);
    resetTypeInForm();
  };

  const data = {
    inactiveProducts: [],
    masterCatalogue: [],
    makers: makersData?.map((maker) => ({
      value: maker.id,
      label: maker.shortName,
    })),
    categories: categoriesData?.data?.map((category) => ({
      value: category.id,
      label: category.categoryName,
    })),
    types: typesData?.data?.map((type) => ({
      value: type.id,
      label: type.name,
    })),
    units: unitsData?.data
      ?.map((unit) => ({ value: unit.id, label: unit.symbol }))
      ?.filter((unit: any) => ['kg', 'L']?.includes(unit.label)),
    groups: groupsData?.data?.map((group) => ({
      value: group.id,
      label: group.groupName,
    })),
  };

  const handleAddProductfromMasterCatalogue = async () => {
    try {
      if (selectedProducts?.length > 0) {
        const payload = {
          vesselId: location.state?.data?.vesselId,
          products: selectedProducts?.map((product) => ({
            productId: product,
            active: true,
          })),
        };

        const res: any = await addPToductToVessel(payload);
        if (res?.data?.success) {
          handleCloseModal();
          setSelectedProducts([]);
          refetchMasterCatalogue();
          showSuccessToast(res?.data?.message);
        }
      } else {
        // handleCloseModal();
        setOpenModal(false);
        setOpenTypeInModal(true);
      }
    } catch (error) {
      setSelectedProducts([]);
      logger.error('Failed to add product', error as Error);
    }
  };

  const handleEditProduct = (data: any) => {
    setEditProductData(data);
    setOpenEditStatusModal(true);
  };

  const handleAddTypeInProduct = async (values: TypeInProductFormValues) => {
    try {
      const managerId = localStorage.getItem('managerId') || 1;

      const createPayload = {
        name: values.productName,
        makerId: values.maker ? Number(values.maker) : null,
        categoryId: values.category ? Number(values.category) : null,
        typeId: values.type ? Number(values.type) : null,
        productGroupId: null,
        typeInManagerId: managerId ? Number(managerId) : null,
        uomId: values.unit ? Number(values.unit) : null,
        status: values.status === 'true' ? true : false,
        typeIn: true,
      };

      const res = await createLubricantProduct(createPayload).unwrap();
      showSuccessToast(res?.message);
      if (res?.success) {
        const payload = {
          vesselId: location.state?.data?.vesselId,
          products: [
            {
              productId: res?.data?.id,
              active: true,
            },
          ],
        };

        const productRes: any = await addPToductToVessel(payload);
        showSuccessToast(productRes?.message);
        if (productRes?.data?.success) {
          setSelectedProducts([]);
          refetchMasterCatalogue();
          // Refetch type-in products list to update sidebar count
          refetchTypeInProducts();
          setOpenTypeInModal(false);
          resetTypeInForm();
        }
      }
    } catch (error) {
      logger.error('Failed to add type-in product', error as Error);
    }
  };

  const handleCloseStatusModal = () => {
    setOpenEditStatusModal(false);
    setEditProductFormData({ productName: '', status: 'false' });
  };

  const handleEditStatus = async () => {
    try {
      const payload = {
        vesselId: location.state?.data?.vesselId,
        productId: editProductData?.id,
        active: editProductFormData?.status === 'true' ? true : false,
        // updatedBy: 1
      };

      const res: any = await updateProduct(payload).unwrap();
      if (res?.success) {
        setOpenEditStatusModal(false);
        showSuccessToast(res?.message);
      }
    } catch (error) {
      logger.error('Failed to edit status', error as Error);
    }
  };

  const handleDeleteProduct = (product: any) => {
    setDeleteTarget(product);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !location.state?.data?.vesselId) return;

    try {
      const response = (await deleteVesselProduct({
        vesselId: location.state.data.vesselId,
        productId: deleteTarget.id,
      }).unwrap()) as any;
      showSuccessToast(response?.message);

      setOpenDeleteModal(false);
      setDeleteTarget(null);
      refetchProducts();
    } catch (e: any) {
      logger.error('Failed to delete vessel product', e as Error);
    }
  };

  return (
    <Stack gap={2} width={'100%'} sx={{ height: 'calc(100vh - 75px)' }}>
      <PageHeader options={breadcrumbOptions} actions={actions} />

      <LubricationProductListing
        lubricationInfo={{
          vessel: rowData.vessel,
          manager: rowData.manager,
          lastUpdatedBy: rowData.lastUpdatedBy,
          lastUpdatedOn: rowData.lastUpdatedDate,
        }}
        mode={mode}
        products={lubricationChartsListData}
        editProduct={handleEditProduct}
        deleteProduct={handleDeleteProduct}
        loading={isLoadingLubricationChartsList}
      />

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        title={t('lubricants.addProduct')}
        size="lg"
        footer={
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="red" onClick={handleCloseModal}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="outlinedBlue" onClick={handleAddProductfromMasterCatalogue}>
              {selectedProducts?.length > 0
                ? t('lubricants.addProducts')
                : t('lubricants.addTypeInProduct')}
            </Button>
          </Box>
        }
      >
        <Box
          ref={containerRef}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ mt: 2, mb: 2, ml: 1 }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '16px',
                color: theme.palette.productAddFlow.heading,
                mb: 1,
              }}
            >
              {t('lubricants.checkMasterList')}
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                color: theme.palette.productAddFlow.subheading,
                fontSize: '15px',
              }}
            >
              {t('lubricants.lubricationMasterCatalogueDescription')}
            </Typography>
          </Box>
          <Box sx={{ height: `${tableHeight}px`, flex: 1 }}>
            <ClientSideAgGrid
              rowData={masterCatalogueList}
              columnDefs={masterCatalogueColumns}
              loading={isLoadingMasterCatalogue}
              className="lubrication-detail-grid"
              onCellClicked={(params: any) => {
                // Only toggle selection when clicking on non-checkbox cells
                // to avoid double-toggle (checkbox onChange + row click)
                if (params.data && params.data.id && params.column.colId !== 'select') {
                  onProductSelect(params.data.id);
                }
              }}
              isInModal={true}
            />
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openEditStatusModal}
        onClose={handleCloseStatusModal}
        title={t('common.editStatus')}
        size="md"
        footer={
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="red" onClick={handleCloseStatusModal}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="outlinedBlue" onClick={handleEditStatus}>
              {t('buttons.save')}
            </Button>
          </Box>
        }
      >
        <Box sx={{ mt: 2, mb: 2, ml: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Typography
              sx={{
                width: '120px',
                fontSize: '14px',
                fontWeight: 500,
                color: theme.palette.formField.label,
              }}
            >
              {t('form.productName')}
            </Typography>
            <InputField
              value={editProductData?.productName}
              required
              sx={{ flex: 1 }}
              placeholder={t('form.enterProductName')}
              disabled={true}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Typography
              sx={{
                width: '120px',
                fontSize: '14px',
                fontWeight: 500,
                color: theme.palette.formField.label,
              }}
            >
              {t('form.status')}
            </Typography>
            <Dropdown
              value={editProductFormData?.status || ''}
              onChange={(e) =>
                setEditProductFormData((prev) => ({
                  ...prev,
                  status: e.target.value,
                }))
              }
              options={statusOptions}
              required
              sx={{ flex: 1 }}
              placeholder={t('common.selectStatus')}
              hasSearch={false}
            />
          </Box>
        </Box>
      </Modal>

      <AddTypeInProductModal
        open={openTypeInModal}
        onClose={handleCloseTypeInModal}
        onSubmit={handleTypeInSubmit(handleAddTypeInProduct)}
        control={typeInControl}
        errors={typeInErrors}
        makersOptions={data?.makers || []}
        categoriesOptions={data?.categories || []}
        typesOptions={data?.types || []}
        unitsOptions={data?.units || []}
        statusOptions={statusOptions}
        isSubmitting={isCreating}
        isFormValid={isTypeInFormValid}
      />

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        open={showUnsavedChangesModal}
        onClose={handleCloseUnsavedChangesModal}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={openDeleteModal}
        title={t('lubricants.deleteProduct')}
        description={t('common.deleteConfirmation')}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isSubmitting={isDeleting}
        confirmLabel={isDeleting ? t('buttons.deleting') : t('buttons.yes')}
      />
    </Stack>
  );
};

export default LubricationDetailContainer;
